import React from "react";
import { Box, useTheme } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import ChecksSeverityBadge from "components/ChecksSeverityBadge";
import { HtmlTooltip } from "components/HeatMap";
import { getWorstCheckSeverity } from "helpers/serviceValidationChecks";

// "Automatic Check" indicator — Amine, 2026-10-08.
//
// Shared by the service grids' Checks columns and the service profiles
// (contract: <ServiceChecksIndicator issues={issues} />):
//   • issues empty  → green check       — "All checks passed"
//   • warnings only → amber warning     — issue list on hover
//   • any error     → red error         — issue list on hover
// The tooltip lists errors before warnings (16px mini severity badge +
// message per issue — the same chip language as the column), capped at
// ~320px so long messages wrap into a compact block.
//
// GLYPH LANGUAGE (Amine 2026-10-13): one glyph set everywhere — plain
// Rounded-family strokes with no circle backgrounds:
//   passed → CheckRounded · warning → PriorityHighRounded (exclamation) ·
//   error → ClearRounded (X). Severity is carried by COLOR (success /
//   warning / error palette values) and chip fill — never by a backdrop.
//
// Two rendering variants (same glyphs, different chrome):
//   • variant="icon" (default, 2026-10-08) — 18px inline colored icon
//     (cursor: help). Used by Translations / OtherServices grid Checks
//     columns and the InterpretationProfile indicator.
//   • variant="chip" (2026-10-13, Amine) — a 16px glyph recolored to
//     the chip's text color, inside a filled chip that mirrors
//     the DataGrid Status columns: error → error.main, warning →
//     warning.main, passed → primary.main (teal). Used only by the
//     Interpretations "Checks" grid column. The chip is a 24px square
//     (perfect square, Amine 2026-10-13) with the 16px glyph centered, and
//     uses the 8px radius shared by every DataGrid status chip
//     (Amine, 2026-10-13) — keep both in sync.
const INDICATOR_ICON_SX = { fontSize: 18, display: "block" };

// Chip variant icon: a slightly smaller plain Rounded-family glyph so it
// sits cleanly inside the 24px chip; the color override (background.default
// — white in light mode, same treatment as the Status text, overriding the
// glyph's color prop) is merged in the component where the theme is
// available.
const CHIP_ICON_SX = { ...INDICATOR_ICON_SX, fontSize: 16 };

// THE glyph map — one set for both variants (Amine 2026-10-13): plain
// Rounded-family strokes (check / exclamation / X) with no circle
// backgrounds. Severity is read from color (icon variant) or the chip fill
// (chip variant) — never from a glyph backdrop.
const GLYPHS = {
  Error: ClearRoundedIcon,
  Warning: PriorityHighRoundedIcon,
  Passed: CheckRoundedIcon,
};

const ServiceChecksIndicator = ({ issues, variant = "icon" }) => {
  const theme = useTheme();
  const isChip = variant === "chip";
  const issueList = Array.isArray(issues) ? issues : [];
  const severity = getWorstCheckSeverity(issueList);

  const iconSx = isChip
    ? { ...CHIP_ICON_SX, color: theme.palette.background.default }
    : INDICATOR_ICON_SX;

  let icon;
  if (severity === "error") {
    icon = <GLYPHS.Error color="error" sx={iconSx} />;
  } else if (severity === "warning") {
    icon = <GLYPHS.Warning color="warning" sx={iconSx} />;
  } else {
    icon = <GLYPHS.Passed color="success" sx={iconSx} />;
  }

  // Errors first, then warnings (Ysera review, 2026-10-08) — the count that
  // matters leads. Stable within each group: the engine emits a deterministic
  // order, and this split preserves it.
  const orderedIssues =
    severity === null
      ? []
      : [
          ...issueList.filter((issue) => issue?.severity === "error"),
          ...issueList.filter((issue) => issue?.severity !== "error"),
        ];

  const tooltipTitle =
    severity === null ? (
      "All checks passed"
    ) : (
      <Box sx={{ maxWidth: 320 }}>
        {orderedIssues.map((issue, index) => (
          <Box
            key={`${issue?.code || "issue"}-${index}`}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "6px",
              "&:not(:last-of-type)": { mb: "4px" },
            }}
          >
            {issue?.severity === "error" ? (
              <ChecksSeverityBadge
                severity="error"
                size={16}
                glyphSize={11}
                radius={4}
                sx={{ mt: "1px" }}
              />
            ) : (
              <ChecksSeverityBadge
                severity="warning"
                size={16}
                glyphSize={11}
                radius={4}
                sx={{ mt: "1px" }}
              />
            )}
            <span>{issue?.message}</span>
          </Box>
        ))}
      </Box>
    );

  // Chip variant — filled chip matching the DataGrid Status columns (8px
  // radius kept in sync there). A 24px square (perfect square, Amine
  // 2026-10-13) with the 16px glyph centered, so the chip no longer
  // stretches across the whole cell. The tooltip stays attached to the chip
  // itself; a Box child forwards the ref HtmlTooltip needs.
  if (isChip) {
    return (
      <HtmlTooltip title={tooltipTitle} arrow placement="top">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            height: 24,
            width: 24,
            lineHeight: 0,
            cursor: "help",
            backgroundColor:
              severity === "error"
                ? theme.palette.error.main
                : severity === "warning"
                  ? theme.palette.warning.main
                  : theme.palette.primary.main,
          }}
        >
          {icon}
        </Box>
      </HtmlTooltip>
    );
  }

  return (
    <HtmlTooltip title={tooltipTitle} arrow placement="top">
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 0,
          cursor: "help",
        }}
      >
        {icon}
      </Box>
    </HtmlTooltip>
  );
};

export default ServiceChecksIndicator;
